import { useState, useEffect, useRef } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { api, InternalControl } from '../lib/api'
import { useAuth } from '../hooks/useAuth'
import { useToast } from '../components/Toast'
import Breadcrumb from '../components/Breadcrumb'
import ScreenCapture from '../components/ScreenCapture'
import {
  Shield,
  Search,
  Filter,
  ChevronDown,
  ChevronRight,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  User,
  Layers,
  BarChart3,
  Loader2,
  X,
  Upload,
  Camera,
  Download,
  Trash2,
} from 'lucide-react'

interface UserOption {
  id: string
  username: string
}

const statusConfig: Record<string, { label: string; color: string; icon: any }> = {
  compliant: { label: 'Compliant', color: 'badge-green', icon: CheckCircle },
  'non-compliant': { label: 'Non-Compliant', color: 'badge-red', icon: XCircle },
  'in-progress': { label: 'In Progress', color: 'badge-blue', icon: Clock },
  pending: { label: 'Pending', color: 'badge-yellow', icon: AlertTriangle },
}

const categoryConfig: Record<string, { label: string; color: string; description: string }> = {
  Identify: {
    label: 'Identify',
    color: 'bg-purple-100 text-purple-800',
    description: 'Asset Management, Risk Assessment',
  },
  Protect: {
    label: 'Protect',
    color: 'bg-blue-100 text-blue-800',
    description: 'Access Control, Data Security, Training',
  },
  Detect: {
    label: 'Detect',
    color: 'bg-amber-100 text-amber-800',
    description: 'Security Monitoring, Detection Processes',
  },
  Respond: {
    label: 'Respond',
    color: 'bg-orange-100 text-orange-800',
    description: 'Incident Response, Communications',
  },
  Recover: {
    label: 'Recover',
    color: 'bg-green-100 text-green-800',
    description: 'Recovery Planning, Improvements',
  },
}

export default function InternalControls() {
  const { user } = useAuth()
  const toast = useToast()
  const [searchParams, setSearchParams] = useSearchParams()
  const [controls, setControls] = useState<InternalControl[]>([])
  const [users, setUsers] = useState<UserOption[]>([])
  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [search, setSearch] = useState(searchParams.get('search') || '')
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '')
  const [categoryFilter, setCategoryFilter] = useState(searchParams.get('category') || '')
  const [ownerFilter, setOwnerFilter] = useState(searchParams.get('owner_id') || '')
  const [showFilters, setShowFilters] = useState(
    !!(searchParams.get('status') || searchParams.get('category') || searchParams.get('owner_id'))
  )
  const [stats, setStats] = useState<{
    by_status: Array<{ status: string; count: number }>
    by_category: Array<{ category: string; count: number }>
    total_evidence_links: number
    coverage: { total_controls: number; with_evidence: number; without_evidence: number }
  } | null>(null)
  const [expandedControl, setExpandedControl] = useState<string | null>(null)
  const [controlDetails, setControlDetails] = useState<Record<string, any>>({})
  const [loadingDetails, setLoadingDetails] = useState<string | null>(null)

  // Evidence upload state
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [uploadControlId, setUploadControlId] = useState<string | null>(null)
  const [uploadControlName, setUploadControlName] = useState<string>('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploadNote, setUploadNote] = useState('')
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState('')
  const [dragActive, setDragActive] = useState<string | null>(null) // Track which control is being dragged over
  const [showScreenCapture, setShowScreenCapture] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const limit = 20

  useEffect(() => {
    async function fetchUsers() {
      try {
        const res = await api.getUsers()
        if (res.success) {
          setUsers(res.users)
        }
      } catch (err) {
        console.error('Failed to load users:', err)
      }
    }
    fetchUsers()
  }, [])

  useEffect(() => {
    async function fetchStats() {
      try {
        const res = await api.getInternalControlStats()
        if (res.success) {
          setStats(res.data)
        }
      } catch (err) {
        console.error('Failed to load stats:', err)
      }
    }
    fetchStats()
  }, [controls])

  useEffect(() => {
    async function fetchControls() {
      setLoading(true)
      try {
        const res = await api.getInternalControls({
          search: search || undefined,
          status: statusFilter || undefined,
          category: categoryFilter || undefined,
          owner_id: ownerFilter || undefined,
          limit,
          offset: page * limit,
        })
        if (res.success) {
          setControls(res.data)
          setTotal(res.pagination.total)
        }
      } catch (err) {
        console.error('Failed to load internal controls:', err)
        toast.error('Failed to load internal controls')
      } finally {
        setLoading(false)
      }
    }
    fetchControls()
  }, [page, search, statusFilter, categoryFilter, ownerFilter])

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams()
    if (search) params.set('search', search)
    if (statusFilter) params.set('status', statusFilter)
    if (categoryFilter) params.set('category', categoryFilter)
    if (ownerFilter) params.set('owner_id', ownerFilter)
    setSearchParams(params, { replace: true })
  }, [search, statusFilter, categoryFilter, ownerFilter])

  const handleExpandControl = async (controlId: string) => {
    if (expandedControl === controlId) {
      setExpandedControl(null)
      return
    }

    setExpandedControl(controlId)

    if (!controlDetails[controlId]) {
      setLoadingDetails(controlId)
      try {
        const res = await api.getInternalControl(controlId)
        if (res.success) {
          setControlDetails((prev) => ({ ...prev, [controlId]: res.data }))
        }
      } catch (err) {
        console.error('Failed to load control details:', err)
        toast.error('Failed to load control details')
      } finally {
        setLoadingDetails(null)
      }
    }
  }

  const clearFilters = () => {
    setSearch('')
    setStatusFilter('')
    setCategoryFilter('')
    setOwnerFilter('')
    setPage(0)
  }

  // Evidence upload handlers
  const handleDrag = (e: React.DragEvent, controlId: string) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(controlId)
    } else if (e.type === 'dragleave') {
      setDragActive(null)
    }
  }

  const handleDrop = (e: React.DragEvent, controlId: string, controlName: string) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(null)
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0])
      setUploadControlId(controlId)
      setUploadControlName(controlName)
      setShowUploadModal(true)
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>, controlId: string, controlName: string) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0])
      setUploadControlId(controlId)
      setUploadControlName(controlName)
      setShowUploadModal(true)
    }
  }

  const openUploadModal = (controlId: string, controlName: string) => {
    setUploadControlId(controlId)
    setUploadControlName(controlName)
    setShowUploadModal(true)
  }

  const handleScreenCapture = (blob: Blob, fileName: string) => {
    const file = new File([blob], fileName, { type: 'image/png' })
    setSelectedFile(file)
    setShowScreenCapture(false)
    setShowUploadModal(true)
  }

  const startScreenCapture = (controlId: string, controlName: string) => {
    setUploadControlId(controlId)
    setUploadControlName(controlName)
    setShowScreenCapture(true)
  }

  const handleEvidenceUpload = async () => {
    if (!uploadControlId || (!selectedFile && !uploadNote.trim())) return

    setUploading(true)
    setUploadError('')

    try {
      const formData = new FormData()
      formData.append('internal_control_id', uploadControlId)
      if (selectedFile) {
        if (selectedFile.size > 50 * 1024 * 1024) {
          throw new Error('File too large. Maximum size is 50MB.')
        }
        formData.append('file', selectedFile)
      }
      if (uploadNote.trim()) {
        formData.append('note', uploadNote.trim())
      }

      const res = await api.uploadEvidence(formData)
      if (res.success) {
        setShowUploadModal(false)
        setSelectedFile(null)
        setUploadNote('')
        setUploadControlId(null)
        setUploadControlName('')
        toast.success('Evidence uploaded successfully')

        // Refresh the control details
        if (uploadControlId && controlDetails[uploadControlId]) {
          const detailRes = await api.getInternalControl(uploadControlId)
          if (detailRes.success) {
            setControlDetails((prev) => ({ ...prev, [uploadControlId]: detailRes.data }))
          }
        }

        // Refresh stats
        const statsRes = await api.getInternalControlStats()
        if (statsRes.success) {
          setStats(statsRes.data)
        }
      }
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  const closeUploadModal = () => {
    setShowUploadModal(false)
    setSelectedFile(null)
    setUploadNote('')
    setUploadError('')
    setUploadControlId(null)
    setUploadControlName('')
  }

  const hasActiveFilters = search || statusFilter || categoryFilter || ownerFilter

  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return 'Never'
    return new Date(timestamp).toLocaleDateString()
  }

  const totalPages = Math.ceil(total / limit)

  return (
    <div className="space-y-6">
      <Breadcrumb
        items={[{ label: 'Internal Controls', href: '/internal-controls' }]}
      />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Internal Controls</h1>
          <p className="text-sm text-gray-500 mt-1">
            Master catalog of controls - evidence attaches here and satisfies framework requirements
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Shield className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-gray-900">
                  {stats.coverage.total_controls}
                </p>
                <p className="text-sm text-gray-500">Total Controls</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-gray-900">
                  {stats.by_status.find((s) => s.status === 'compliant')?.count || 0}
                </p>
                <p className="text-sm text-gray-500">Compliant</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <FileText className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-gray-900">
                  {stats.total_evidence_links}
                </p>
                <p className="text-sm text-gray-500">Evidence Links</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <BarChart3 className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-gray-900">
                  {stats.coverage.with_evidence > 0
                    ? Math.round((stats.coverage.with_evidence / stats.coverage.total_controls) * 100)
                    : 0}%
                </p>
                <p className="text-sm text-gray-500">Evidence Coverage</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(0)
              }}
              placeholder="Search by control number or name..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
              hasActiveFilters
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-gray-300 hover:bg-gray-50'
            }`}
          >
            <Filter className="w-4 h-4" />
            Filters
            {hasActiveFilters && (
              <span className="bg-primary-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                {[statusFilter, categoryFilter, ownerFilter].filter(Boolean).length}
              </span>
            )}
            {showFilters ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
            >
              <X className="w-4 h-4" />
              Clear
            </button>
          )}
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value)
                  setPage(0)
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">All Statuses</option>
                <option value="compliant">Compliant</option>
                <option value="non-compliant">Non-Compliant</option>
                <option value="in-progress">In Progress</option>
                <option value="pending">Pending</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value)
                  setPage(0)
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">All Categories</option>
                <option value="Identify">Identify</option>
                <option value="Protect">Protect</option>
                <option value="Detect">Detect</option>
                <option value="Respond">Respond</option>
                <option value="Recover">Recover</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Owner</label>
              <select
                value={ownerFilter}
                onChange={(e) => {
                  setOwnerFilter(e.target.value)
                  setPage(0)
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">All Owners</option>
                <option value="unassigned">Unassigned</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.username}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Controls List */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
          </div>
        ) : controls.length === 0 ? (
          <div className="text-center py-12">
            <Shield className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No internal controls found</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {controls.map((control) => {
              const status = statusConfig[control.status] || statusConfig.pending
              const category = categoryConfig[control.category] || categoryConfig.Identify
              const isExpanded = expandedControl === control.id
              const details = controlDetails[control.id]

              return (
                <div key={control.id} className="hover:bg-gray-50">
                  <div
                    className="px-6 py-4 cursor-pointer"
                    onClick={() => handleExpandControl(control.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="flex-shrink-0">
                          {isExpanded ? (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3">
                            <span className="font-mono text-sm text-gray-600">
                              {control.control_number}
                            </span>
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${category.color}`}>
                              {control.category}
                            </span>
                          </div>
                          <p className="font-medium text-gray-900 truncate">{control.name}</p>
                          {control.subcategory && (
                            <p className="text-sm text-gray-500">{control.subcategory}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-6 flex-shrink-0">
                        <div className="text-center">
                          <p className="text-lg font-semibold text-gray-900">
                            {control.evidence_count || 0}
                          </p>
                          <p className="text-xs text-gray-500">Evidence</p>
                        </div>
                        <div className="text-center">
                          <p className="text-lg font-semibold text-gray-900">
                            {control.policy_count || 0}
                          </p>
                          <p className="text-xs text-gray-500">Policies</p>
                        </div>
                        <div className="text-center">
                          <p className="text-lg font-semibold text-gray-900">
                            {control.framework_control_count || 0}
                          </p>
                          <p className="text-xs text-gray-500">Framework</p>
                        </div>
                        <div className="text-right">
                          <div className={`${status.color} px-3 py-1 rounded-full flex items-center gap-1.5`}>
                            {status.icon && <status.icon className="w-4 h-4" />}
                            <span className="text-sm font-medium">{status.label}</span>
                          </div>
                          {control.status_reason && (
                            <p className="text-xs text-gray-500 mt-1 max-w-[200px] truncate" title={control.status_reason}>
                              {control.status_reason}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                      {loadingDetails === control.id ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
                        </div>
                      ) : details ? (
                        <div className="space-y-6">
                          {/* Control Info */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <p className="text-sm font-medium text-gray-500">Frequency</p>
                              <p className="text-sm text-gray-900 capitalize">{control.frequency}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-500">Owner</p>
                              <p className="text-sm text-gray-900">
                                {control.owner_username || 'Unassigned'}
                              </p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-500">Last Evidence</p>
                              <p className="text-sm text-gray-900">
                                {formatDate(control.last_evidence_date)}
                              </p>
                            </div>
                          </div>

                          {control.description && (
                            <div>
                              <p className="text-sm font-medium text-gray-500 mb-1">Description</p>
                              <p className="text-sm text-gray-700">{control.description}</p>
                            </div>
                          )}

                          {/* Issue #312: Evidence Guidance Section */}
                          {control.evidence_guidance && (
                            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                              <p className="text-sm font-medium text-purple-700 mb-1 flex items-center gap-1.5">
                                <FileText className="w-4 h-4" />
                                Evidence Required
                              </p>
                              <p className="text-sm text-purple-900">{control.evidence_guidance}</p>
                            </div>
                          )}

                          {/* Evidence Section */}
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-sm font-medium text-gray-500">
                                Recent Evidence ({details.evidence_count})
                              </p>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    startScreenCapture(control.id, control.name)
                                  }}
                                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                  <Camera className="w-4 h-4" />
                                  Capture
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    openUploadModal(control.id, control.name)
                                  }}
                                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary-600 text-white hover:bg-primary-700 rounded-lg transition-colors"
                                >
                                  <Upload className="w-4 h-4" />
                                  Upload
                                </button>
                              </div>
                            </div>

                            {/* Drop zone */}
                            <div
                              onDragEnter={(e) => handleDrag(e, control.id)}
                              onDragLeave={(e) => handleDrag(e, control.id)}
                              onDragOver={(e) => handleDrag(e, control.id)}
                              onDrop={(e) => handleDrop(e, control.id, control.name)}
                              onClick={(e) => e.stopPropagation()}
                              className={`border-2 border-dashed rounded-lg p-4 mb-3 text-center transition-colors ${
                                dragActive === control.id
                                  ? 'border-primary-500 bg-primary-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <Upload className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                              <p className="text-sm text-gray-500">
                                Drag and drop files here or{' '}
                                <label className="text-primary-600 hover:text-primary-700 cursor-pointer">
                                  browse
                                  <input
                                    type="file"
                                    className="hidden"
                                    onChange={(e) => handleFileSelect(e, control.id, control.name)}
                                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.json,.xml"
                                  />
                                </label>
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                Max 50MB • Images, PDFs, Documents
                              </p>
                            </div>

                            {details.evidence && details.evidence.length > 0 ? (
                              <div className="space-y-2">
                                {details.evidence.slice(0, 5).map((e: any) => (
                                  <div
                                    key={e.id}
                                    className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-200"
                                  >
                                    <div className="flex items-center gap-3">
                                      <FileText className="w-4 h-4 text-gray-400" />
                                      <div>
                                        <p className="text-sm font-medium text-gray-900">
                                          {e.file_name || 'Evidence'}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                          {formatDate(e.uploaded_at)} by {e.uploaded_by_username || 'Unknown'}
                                        </p>
                                      </div>
                                    </div>
                                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                                      {e.source || 'manual'}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-400 italic">No evidence uploaded yet</p>
                            )}
                          </div>

                          {/* Linked Policies Section - #311 */}
                          {details.policies && details.policies.length > 0 && (
                            <div>
                              <p className="text-sm font-medium text-gray-500 mb-2">
                                Linked Policies ({details.policy_count})
                              </p>
                              <div className="space-y-2">
                                {details.policies.map((p: any) => (
                                  <div
                                    key={p.id}
                                    className="flex items-center justify-between bg-green-50 p-3 rounded-lg border border-green-200"
                                  >
                                    <div className="flex items-center gap-3">
                                      <FileText className="w-4 h-4 text-green-600" />
                                      <div>
                                        <p className="text-sm font-medium text-gray-900">
                                          {p.name}
                                        </p>
                                        {p.approved_at && (
                                          <p className="text-xs text-gray-500">
                                            Approved {formatDate(p.approved_at)}
                                            {p.approved_by_username && ` by ${p.approved_by_username}`}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                    <span className={`text-xs px-2 py-1 rounded ${
                                      p.status === 'approved'
                                        ? 'bg-green-100 text-green-700'
                                        : 'bg-gray-100 text-gray-600'
                                    }`}>
                                      {p.status}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Framework Controls Section */}
                          <div>
                            <p className="text-sm font-medium text-gray-500 mb-2">
                              Satisfies Framework Requirements ({details.framework_control_count})
                            </p>
                            {details.framework_controls && details.framework_controls.length > 0 ? (
                              <div className="flex flex-wrap gap-2">
                                {details.framework_controls.slice(0, 10).map((fc: any) => (
                                  <Link
                                    key={fc.id}
                                    to={`/controls/${fc.id}`}
                                    className="inline-flex items-center gap-1.5 bg-white border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <span className="text-xs font-medium text-gray-600">
                                      {fc.framework_code}
                                    </span>
                                    <span className="text-xs text-gray-400">|</span>
                                    <span className="text-sm text-gray-900">{fc.control_number}</span>
                                  </Link>
                                ))}
                                {details.framework_control_count > 10 && (
                                  <span className="text-sm text-gray-500 py-1.5">
                                    +{details.framework_control_count - 10} more
                                  </span>
                                )}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-400 italic">No framework mappings</p>
                            )}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total} controls
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {page + 1} of {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                disabled={page >= totalPages - 1}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={closeUploadModal} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <button
              onClick={closeUploadModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>

            <h3 className="text-lg font-semibold text-gray-900 mb-1">Upload Evidence</h3>
            <p className="text-sm text-gray-500 mb-4">
              Attaching to: <span className="font-medium text-gray-700">{uploadControlName}</span>
            </p>

            {uploadError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                {uploadError}
              </div>
            )}

            {/* File Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">File</label>
              {selectedFile ? (
                <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                      <p className="text-xs text-gray-500">
                        {(selectedFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedFile(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-primary-500 hover:bg-primary-50 transition-colors"
                >
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">Click to select a file</p>
                  <p className="text-xs text-gray-400 mt-1">Max 50MB</p>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    setSelectedFile(e.target.files[0])
                  }
                }}
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.json,.xml"
              />
            </div>

            {/* Note */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Note <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea
                value={uploadNote}
                onChange={(e) => setUploadNote(e.target.value)}
                placeholder="Add a description of this evidence..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 resize-none"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <button
                onClick={closeUploadModal}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleEvidenceUpload}
                disabled={uploading || (!selectedFile && !uploadNote.trim())}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-primary-600 text-white hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Upload
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Screen Capture */}
      {showScreenCapture && uploadControlId && (
        <ScreenCapture
          onCapture={handleScreenCapture}
          onCancel={() => {
            setShowScreenCapture(false)
            setUploadControlId(null)
            setUploadControlName('')
          }}
        />
      )}
    </div>
  )
}
